/*
 * Parallax (original scrolling-in-view + mouse-move interactions).
 *
 * Scroll parallax: elements drift on a scrubbed timeline tied to their own
 * viewport progress. Opt in with data attributes so any page can use it:
 *   data-parallax="y" data-parallax-from="0" data-parallax-to="40" data-parallax-unit="px"
 *
 * Mouse parallax: layers inside a [data-mouse-scene] shift with the pointer,
 * each by its own data-mouse-depth (in px).
 */
import type { gsap as GSAP } from 'gsap'

import { gsap } from './core'

const num = (v: string | null, fallback: number): number => {
    const n = v == null ? NaN : parseFloat(v)
    return Number.isFinite(n) ? n : fallback
}

export const initScrollParallax = (ctx: GSAP.Context, scope: HTMLElement): void => {
    ctx.add(() => {
        // Generic, data-driven scroll drift.
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('[data-parallax]')).forEach((el) => {
            const axis = (el.dataset.parallax === 'x' ? 'x' : 'y') as 'x' | 'y'
            const unit = el.dataset.parallaxUnit || 'px'
            const from = num(el.dataset.parallaxFrom ?? null, 0)
            const to = num(el.dataset.parallaxTo ?? null, axis === 'y' ? 40 : -40)

            gsap.fromTo(
                el,
                { [axis]: `${from}${unit}` },
                {
                    [axis]: `${to}${unit}`,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                }
            )
        })

        // Showcase cards drift down slightly as they pass through (a-72).
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.showcase-item-link')).forEach((el) => {
            gsap.fromTo(
                el,
                { y: 0 },
                {
                    y: 20,
                    ease: 'none',
                    scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
                }
            )
        })
    })
}

export const initMouseParallax = (ctx: GSAP.Context, scope: HTMLElement): void => {
    ctx.add(() => {
        const scenes = gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('[data-mouse-scene]'))
        const cleanups: Array<() => void> = []

        scenes.forEach((scene) => {
            const layers = gsap.utils.toArray<HTMLElement>(scene.querySelectorAll('[data-mouse-depth]'))
            if (!layers.length) return

            const movers = layers.map((layer) => ({
                depth: num(layer.dataset.mouseDepth ?? null, 12),
                xTo: gsap.quickTo(layer, 'x', { duration: 0.6, ease: 'power3.out' }),
                yTo: gsap.quickTo(layer, 'y', { duration: 0.6, ease: 'power3.out' })
            }))

            const onMove = (e: MouseEvent) => {
                const rect = scene.getBoundingClientRect()
                const nx = (e.clientX - rect.left) / rect.width - 0.5
                const ny = (e.clientY - rect.top) / rect.height - 0.5
                movers.forEach((m) => {
                    m.xTo(-nx * m.depth)
                    m.yTo(-ny * m.depth)
                })
            }
            const onLeave = () => movers.forEach((m) => { m.xTo(0); m.yTo(0) })

            scene.addEventListener('mousemove', onMove)
            scene.addEventListener('mouseleave', onLeave)

            cleanups.push(() => {
                scene.removeEventListener('mousemove', onMove)
                scene.removeEventListener('mouseleave', onLeave)
            })
        })

        return () => cleanups.forEach((fn) => fn())
    })
}
